// src/components/adminpanel/AccountManagement.tsx
import { useState, useEffect } from "react";
import { useAuthStore } from "@/store/authStore";
import { db } from "@/lib/firebase";
import { collection, onSnapshot, doc, updateDoc } from "firebase/firestore";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "@/hooks/use-toast";
import { Eye, EyeOff, Save, Users, ArrowLeft, Lock, X, MessageCircle, Pencil } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { cn } from "@/lib/utils";

interface User {
  id: string;
  numericId: number;
  login: string;
  password: string;
  name: string;
  role: string;
  isAdmin: boolean;
  canCreateTask: boolean;
  active: boolean;
  telegramId?: string;
}

export default function AccountManagement() {
  const { user } = useAuthStore();
  const navigate = useNavigate();

  const [users, setUsers] = useState<User[]>([]);
  const [editingPasswordId, setEditingPasswordId] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState("");

  // Редактирование всей строки
  const [editingRowId, setEditingRowId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<Partial<User>>({});

  const [showAllPasswords, setShowAllPasswords] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  if (!user?.isAdmin) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-slate-950 via-purple-950 to-indigo-950">
        <div className="text-center">
          <Users className="h-20 w-20 text-red-500 mx-auto mb-6" />
          <h1 className="text-5xl font-bold text-white mb-4">Доступ запрещён</h1>
          <p className="text-purple-300 text-xl">Эта страница доступна только Администратору</p>
        </div>
      </div>
    );
  }

  useEffect(() => {
    const unsubscribe = onSnapshot(collection(db, "users"), (snapshot) => {
      const loaded = snapshot.docs.map(d => ({
        id: d.id,
        ...d.data()
      })) as User[];
      setUsers(loaded.sort((a, b) => a.numericId - b.numericId));
    });
    return () => unsubscribe();
  }, []);

  const filteredUsers = users.filter(u => 
    u.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    u.login.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (u.telegramId && u.telegramId.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const handleSaveRow = async (userId: string) => {
    if (!editForm) return;
    try {
      await updateDoc(doc(db, "users", userId), {
        name: editForm.name,
        login: editForm.login,
        role: editForm.role,
        telegramId: editForm.telegramId || null,
        isAdmin: editForm.isAdmin,
        canCreateTask: editForm.canCreateTask,
        active: editForm.active,
      });
      toast({ title: "✅ Данные сохранены" });
      setEditingRowId(null);
      setEditForm({});
    } catch (err) {
      toast({ title: "Ошибка сохранения", variant: "destructive" });
    }
  };

  const handleSavePassword = async (userId: string) => {
    if (!newPassword.trim()) {
      toast({ title: "Ошибка", description: "Введите новый пароль", variant: "destructive" });
      return;
    }
    try {
      await updateDoc(doc(db, "users", userId), { password: newPassword.trim() });
      toast({ title: "Пароль успешно изменён" });
      setEditingPasswordId(null);
      setNewPassword("");
    } catch (err) {
      toast({ title: "Ошибка при смене пароля", variant: "destructive" });
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-950 via-purple-950 to-indigo-950 p-6 md:p-10">
      <div className="max-w-full mx-auto">

        {/* Заголовок */}
        <div className="flex items-center gap-4 mb-10">
          <Button variant="ghost" onClick={() => navigate("/admin")} className="text-purple-300 hover:text-white">
            <ArrowLeft className="mr-2 h-5 w-5" /> Назад
          </Button>
          <div className="flex-1" />
          <div className="flex items-center gap-3">
            <Users className="h-9 w-9 text-purple-400" />
            <h1 className="text-4xl font-bold text-white">Управление аккаунтами</h1>
          </div>
        </div>

        {/* Поиск */}
        <div className="max-w-md mb-8">
          <Input
            placeholder="Поиск по имени, логину или Telegram ID..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="bg-white/10 border-white/30 text-white placeholder:text-purple-400 h-14 text-lg rounded-2xl"
          />
        </div>

        <div className="bg-slate-900/80 backdrop-blur-2xl border border-white/10 rounded-3xl overflow-hidden shadow-2xl">
          <div className="p-8">
            <div className="flex justify-between items-center mb-8">
              <h2 className="text-3xl font-bold text-white">Список пользователей</h2>

              <Button
                onClick={() => setShowAllPasswords(!showAllPasswords)}
                className={cn(
                  "flex items-center gap-2 px-6 py-2.5 rounded-2xl font-medium transition-all duration-300",
                  showAllPasswords 
                    ? "bg-red-500/20 hover:bg-red-500/30 text-red-400 border border-red-500/40" 
                    : "bg-purple-600/20 hover:bg-purple-600/30 text-purple-300 border border-purple-500/40"
                )}
              >
                {showAllPasswords ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                {showAllPasswords ? "Скрыть все пароли" : "Показать все пароли"}
              </Button>
            </div>

            <div className="space-y-4">
              {filteredUsers.map((u) => {
                const isEditingPassword = editingPasswordId === u.id;
                const isEditingRow = editingRowId === u.id;

                return (
                  <div
                    key={u.id}
                    className="bg-white/5 border border-white/10 rounded-2xl p-6 hover:border-purple-500/50 transition-all"
                  >
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-center">

                      {/* ФИО + Логин */}
                      <div className="md:col-span-3 flex items-center gap-6">
                        <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-purple-500 to-pink-500 flex items-center justify-center text-2xl font-bold flex-shrink-0">
                          {u.name.split(" ").map(n => n[0]).join("")}
                        </div>
                        <div className="flex-1 min-w-0">
                          {isEditingRow ? (
                            <>
                              <Input
                                value={editForm.name || ""}
                                onChange={(e) => setEditForm({ ...editForm, name: e.target.value })}
                                className="bg-white/10 border-purple-400 text-white font-semibold mb-1"
                              />
                              <Input
                                value={editForm.login || ""}
                                onChange={(e) => setEditForm({ ...editForm, login: e.target.value })}
                                className="bg-white/10 border-purple-400 text-purple-300 text-sm"
                                placeholder="Логин"
                              />
                            </>
                          ) : (
                            <>
                              <h3 className="text-xl font-semibold text-white">{u.name}</h3>
                              <p className="text-purple-300">@{u.login} • ID: {u.numericId}</p>
                            </>
                          )}
                        </div>
                      </div>

                      {/* Роль */}
                      <div className="md:col-span-2">
                        <p className="text-sm text-purple-400 mb-1">Роль</p>
                        {isEditingRow ? (
                          <Input
                            value={editForm.role || ""}
                            onChange={(e) => setEditForm({ ...editForm, role: e.target.value })}
                            className="bg-white/10 border-purple-400 text-white"
                          />
                        ) : (
                          <p className="font-medium text-white">{u.role}</p>
                        )}
                      </div>

                      {/* Права */}
                      <div className="md:col-span-2">
                        <p className="text-sm text-purple-400 mb-1">Права</p>
                        {isEditingRow ? (
                          <div className="flex flex-col gap-1 text-sm">
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input type="checkbox" checked={!!editForm.isAdmin} onChange={(e) => setEditForm({ ...editForm, isAdmin: e.target.checked })} />
                              Администратор
                            </label>
                            <label className="flex items-center gap-2 cursor-pointer">
                              <input type="checkbox" checked={!!editForm.canCreateTask} onChange={(e) => setEditForm({ ...editForm, canCreateTask: e.target.checked })} />
                              Создание задач
                            </label>
                          </div>
                        ) : (
                          <span className={`px-4 py-1 rounded-full text-sm ${u.isAdmin ? "bg-red-500/20 text-red-400" : "bg-emerald-500/20 text-emerald-400"}`}>
                            {u.isAdmin ? "Администратор" : u.canCreateTask ? "Создание задач" : "Обычный"}
                          </span>
                        )}
                      </div>

                      {/* Telegram ID */}
                      <div className="md:col-span-2">
                        <p className="text-sm text-purple-400 mb-1 flex items-center gap-1">
                          <MessageCircle className="h-4 w-4" /> Telegram
                        </p>
                        {isEditingRow ? (
                          <Input
                            value={editForm.telegramId || ""}
                            onChange={(e) => setEditForm({ ...editForm, telegramId: e.target.value })}
                            className="bg-white/10 border-purple-400 text-white"
                            placeholder="@username"
                          />
                        ) : u.telegramId ? (
                          <a href={`https://t.me/${u.telegramId}`} target="_blank" rel="noopener noreferrer" className="text-blue-400 hover:text-blue-300 hover:underline font-mono">
                            @{u.telegramId}
                          </a>
                        ) : (
                          <span className="text-gray-500 text-sm">—</span>
                        )}
                      </div>

                      {/* Пароль */}
                      <div className="md:col-span-1">
                        <p className="text-sm text-purple-400 mb-1">Пароль</p>
                        <p className="font-mono text-lg text-white tracking-widest">
                          {showAllPasswords ? u.password : "••••••"}
                        </p>
                      </div>

                      {/* Действия */}
                      <div className="md:col-span-2 flex items-center gap-2">
                        {/* Кнопка карандаш (современный стиль) */}
                        {!isEditingRow && (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              setEditingRowId(u.id);
                              setEditForm({ ...u });
                            }}
                            className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 text-white border-none shadow-md transition-all duration-300"
                          >
                            <Pencil className="h-4 w-4" />
                          </Button>
                        )}

                        {/* Кнопки сохранения при редактировании */}
                        {isEditingRow && (
                          <>
                            <Button 
                              onClick={() => handleSaveRow(u.id)} 
                              className="bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-700 hover:to-teal-700 text-white"
                            >
                              <Save className="h-4 w-4 mr-1" /> Сохранить
                            </Button>
                            <Button 
                              variant="ghost" 
                              onClick={() => { 
                                setEditingRowId(null); 
                                setEditForm({}); 
                              }}
                              className="text-purple-300 hover:text-white"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </>
                        )}

                        {/* Кнопка "Изменить пароль" — оригинальная */}
                        {isEditingPassword ? (
                          <div className="flex flex-col sm:flex-row gap-3 w-full">
                            <Input
                              type="text"
                              value={newPassword}
                              onChange={(e) => setNewPassword(e.target.value)}
                              placeholder="Новый пароль"
                              className="bg-white/10 border-purple-400/60 text-white placeholder:text-purple-400 focus:border-purple-400 h-12 text-base flex-1 min-w-0"
                              autoFocus
                            />
                            <div className="flex gap-2 flex-shrink-0">
                              <Button onClick={() => handleSavePassword(u.id)} className="bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 h-12 px-5 rounded-2xl">
                                <Save className="h-5 w-5" />
                              </Button>
                              <Button variant="ghost" onClick={() => { setEditingPasswordId(null); setNewPassword(""); }} className="h-12 px-5">
                                <X className="h-5 w-5" />
                              </Button>
                            </div>
                          </div>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => setEditingPasswordId(u.id)}
                            className={cn(
                              "group relative overflow-hidden border border-purple-500/70 w-full h-12",
                              "hover:border-purple-400 hover:bg-purple-600/10",
                              "px-6 py-2.5 rounded-2xl font-medium transition-all duration-300",
                              "flex items-center justify-center gap-2 text-purple-300 hover:text-white"
                            )}
                          >
                            <Lock className="h-4 w-4 group-hover:rotate-12 transition-transform" />
                            Изменить пароль
                          </Button>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}